import { motion } from "framer-motion";
import { TrendingUp, Target, Sparkles, Zap } from "lucide-react";
import { TREND_POINTS, formatRevenue, trendPath, trendPoint } from "./revenueModel";

export const RevenueGauge = ({ progressPercentage }) => (
  <div className="flex flex-col items-center justify-center">
    <div className="relative" style={{ width: "200px", height: "100px" }}>
      <svg className="w-full h-full" viewBox="0 0 200 100">
        <path d="M 20 100 A 80 80 0 0 1 180 100" fill="none" stroke="rgba(255,255,255,0.05)" strokeWidth="16" strokeLinecap="round" />
        <path d="M 20 100 A 80 80 0 0 1 180 100" fill="none" stroke="url(#gaugeGradientNeon)" strokeWidth="16" strokeLinecap="round"
          strokeDasharray={`${(Math.min(100, progressPercentage) / 100) * 251.2} 251.2`} filter="url(#glow)" />
        <defs>
          <linearGradient id="gaugeGradientNeon" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor="#00F0FF" /><stop offset="100%" stopColor="#FF0099" />
          </linearGradient>
          <filter id="glow" x="-50%" y="-50%" width="200%" height="200%">
            <feGaussianBlur stdDeviation="4" result="coloredBlur" />
            <feMerge><feMergeNode in="coloredBlur" /><feMergeNode in="SourceGraphic" /></feMerge>
          </filter>
        </defs>
      </svg>
      <div className="absolute inset-0 flex items-center justify-center">
        <div className="text-center mt-6">
          <p className="text-4xl text-ism-teal data-value">{Math.round(progressPercentage)}%</p>
          <p className="text-xs text-gray-500 tracking-wider mt-1">Progress</p>
        </div>
      </div>
    </div>
  </div>
);

const StatCard = ({ label, value, icon: Icon, iconAnim, valueCls, bar }) => (
  <motion.div className="glass-card-light p-4" whileHover={{ scale: 1.02, y: -2 }} transition={{ type: "spring", stiffness: 400 }}>
    <div className="flex items-center justify-between mb-2">
      <span className="text-xs text-gray-500 tracking-wider">{label}</span>
      <motion.div className="p-1.5 rounded-lg bg-ism-teal/10" {...iconAnim}><Icon className="h-4 w-4 text-ism-teal" /></motion.div>
    </div>
    <p className={`text-2xl data-value ${valueCls}`}>{value}</p>
    {bar !== undefined && (
      <div className="mt-2 h-1 rounded-full bg-white/10 overflow-hidden">
        <motion.div className="h-full bg-gradient-to-r from-ism-teal to-ism-teal/50" initial={{ width: 0 }} animate={{ width: `${bar}%` }} transition={{ duration: 1, delay: 0.2 }} />
      </div>
    )}
  </motion.div>
);

export const RevenueBreakdown = ({ current, remaining, progressPercentage }) => (
  <div className="space-y-4">
    <StatCard label="Current Revenue" value={formatRevenue(current)} icon={TrendingUp} valueCls="font-bold text-white" bar={progressPercentage}
      iconAnim={{ animate: { rotate: [0, 10, -10, 0] }, transition: { duration: 3, repeat: Infinity } }} />
    <StatCard label="Remaining" value={formatRevenue(remaining)} icon={Target} valueCls="text-ism-teal"
      iconAnim={{ animate: { scale: [1, 1.1, 1] }, transition: { duration: 2, repeat: Infinity } }} />
  </div>
);

export const RevenueTrendChart = ({ points = TREND_POINTS }) => (
  <div>
    <div className="flex items-center justify-between mb-4">
      <div>
        <p className="text-xs text-gray-500 tracking-wider">Performance Trend</p>
        <div className="flex items-center gap-2 mt-1"><Sparkles className="h-4 w-4 text-ism-teal" /><p className="text-sm font-bold text-ism-teal">+24% Growth</p></div>
      </div>
      <motion.div className="p-2 rounded-lg bg-white/5" animate={{ rotate: 360 }} transition={{ duration: 20, repeat: Infinity, ease: "linear" }}><Zap className="h-4 w-4 text-amber-400" /></motion.div>
    </div>
    <div className="relative h-32 glass-card-light rounded-lg p-4">
      <svg className="w-full h-full" viewBox="0 0 300 120" preserveAspectRatio="none">
        <defs>
          <linearGradient id="chartGradientNeon" x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" stopColor="#00F0FF" stopOpacity="0.4" /><stop offset="100%" stopColor="#00F0FF" stopOpacity="0" />
          </linearGradient>
          <linearGradient id="lineGradient" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor="#00F0FF" /><stop offset="100%" stopColor="#FF0099" />
          </linearGradient>
        </defs>
        <motion.path d={trendPath(points, true)} fill="url(#chartGradientNeon)" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 1 }} />
        <motion.path d={trendPath(points)} fill="none" stroke="url(#lineGradient)" strokeWidth="3" strokeLinecap="round" filter="url(#glow)"
          initial={{ pathLength: 0 }} animate={{ pathLength: 1 }} transition={{ duration: 1.5, ease: "easeInOut" }} />
        {points.map((val, idx) => (
          <motion.circle key={`pt-${idx}-${val}`} {...trendPoint(points, idx)} r="4" fill="#050505" stroke="#00F0FF" strokeWidth="2"
            initial={{ scale: 0, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} transition={{ delay: 0.1 * idx, duration: 0.3 }} />
        ))}
      </svg>
    </div>
  </div>
);
